import { ReactNode } from "react";
import styled from "styled-components";

import MemberGroupSection from "./MemberGroupSection";
import UserListRow from "../user-item/UserListRow";
import { useIsUserOnline } from "../../hooks/useIsUserOnline";
import {
  CLIENT_GROUP_POSITION_ORDER,
  PRODUCER_GROUP_POSITION_ORDER,
  isClientGroupPosition,
  isPrimaryPosition,
  isProducerGroupPosition,
  isSpecialistPosition,
  orderMembersByPosition,
} from "../../utils/memberPositionUtils";

/**
 * プロジェクトメンバーの「グループ別リスト」（閲覧用）。
 *
 * プロジェクトバナーのユーザーリスト（`project-banner/components/UserList.jsx`）
 * と同じ見た目（クライアント青 / プロデューサー灰 / スペシャリスト枠 +
 * `UserListRow` の flat 行）を、管理画面のメンバーシップモーダル等でも
 * 使い回すためのグローバル部品。
 *
 * バナー側は DnD 並び替えや招待などの操作が絡むため、本コンポーネントは
 * 使わず `MemberGroupSection` + `UserListRow` を直接組み合わせている。
 * 行に操作を足したい場合は `renderActions` で trailingSlot を注入する。
 */
export interface MemberGroupListMember {
  user_id?: number | string | null;
  position: string;
  avatar_url?: string | null;
  display_name_jp?: string | null;
  display_name_en?: string | null;
  title_jp?: string | null;
  affiliation_jp?: string | null;
}

export interface MemberGroupListProps<
  T extends MemberGroupListMember = MemberGroupListMember,
> {
  members: T[];
  /**
   * スペシャリストグループを表示するか。バナーではクライアントに非公開
   * （PRODUCER / DIRECTOR のみ閲覧可）。管理画面など全件見せる文脈は true。
   * 既定 true。
   */
  showSpecialists?: boolean;
  /** 行の右端（trailingSlot）に出す操作要素 */
  renderActions?: (_member: T) => ReactNode;
  className?: string;
}

/**
 * 非責任者行のインデント（バナーの `marginLeft: 16` 相当）。
 * leadingSlot(8px) + 行内 gap(8px) でアバター左端が 16px 下がる。
 */
export const MemberIndentSpacer = styled.span`
  display: inline-block;
  width: 8px;
  flex-shrink: 0;
`;

interface MemberRowProps<T extends MemberGroupListMember> {
  member: T;
  renderActions?: (_member: T) => ReactNode;
}

/** 1 メンバー行。オンライン判定フックを使うためコンポーネントに分離。 */
const MemberRow = <T extends MemberGroupListMember>({
  member,
  renderActions,
}: MemberRowProps<T>) => {
  const isOnline = useIsUserOnline(member.user_id);
  const isPrimary = isPrimaryPosition(member.position);
  const actions = renderActions?.(member);

  return (
    <UserListRow
      variant="flat"
      user={{
        avatar_url: member.avatar_url,
        display_name: member.display_name_jp || member.display_name_en || "",
        title: `${member.title_jp || ""}${isPrimary ? "（責任者）" : ""}`,
        affiliation: isPrimary ? member.affiliation_jp : undefined,
      }}
      avatarSize={isPrimary ? 48 : 32}
      isOnline={isOnline}
      leadingSlot={!isPrimary ? <MemberIndentSpacer /> : undefined}
      trailingSlot={actions || undefined}
    />
  );
};

const MemberGroupList = <T extends MemberGroupListMember>({
  members,
  showSpecialists = true,
  renderActions,
  className,
}: MemberGroupListProps<T>) => {
  const clientMembers = orderMembersByPosition(
    members.filter((m) => isClientGroupPosition(m.position)),
    CLIENT_GROUP_POSITION_ORDER,
  );
  const producerMembers = orderMembersByPosition(
    members.filter((m) => isProducerGroupPosition(m.position)),
    PRODUCER_GROUP_POSITION_ORDER,
  );
  const specialistMembers = members.filter((m) =>
    isSpecialistPosition(m.position),
  );

  const renderRows = (groupMembers: T[]) =>
    groupMembers.map((member, index) => (
      <MemberRow
        key={member.user_id ?? `member-${index}`}
        member={member}
        renderActions={renderActions}
      />
    ));

  return (
    <div className={`u-col u-gap4 u-w100 ${className ?? ""}`.trim()}>
      {clientMembers.length > 0 && (
        <MemberGroupSection variant="client">
          {renderRows(clientMembers)}
        </MemberGroupSection>
      )}
      {producerMembers.length > 0 && (
        <MemberGroupSection variant="producer">
          {renderRows(producerMembers)}
        </MemberGroupSection>
      )}
      {showSpecialists && specialistMembers.length > 0 && (
        <MemberGroupSection variant="specialist">
          {renderRows(specialistMembers)}
        </MemberGroupSection>
      )}
    </div>
  );
};

export default MemberGroupList;
